import React, { createContext, type ReactNode, useContext, useMemo } from 'react'
import { ChangeGroupImageOverlay } from '@fs/zion-group-management'
import { useOverlay, type UseOverlayReturnOptions } from '@fs/zion-ui'
import { type TreeGroupDetails } from '@fs/zion-tree-types'

type TreeGroupDetailsValue = {
  group: TreeGroupDetails
  refetchGroup: () => void
  onLeaveGroup: () => void
  changeImageOverlay: UseOverlayReturnOptions
}

const TreeGroupDetailsContext = createContext<TreeGroupDetailsValue | undefined>(undefined)

export const useTreeGroupDetailsContext = (): TreeGroupDetailsValue => {
  const context = useContext(TreeGroupDetailsContext)

  if (!context) {
    throw new Error('useTreeGroupDetailsContext must be used within a TreeGroupDetailsProvider')
  }

  return context
}

type TreeGroupDetailsProviderProps = Omit<TreeGroupDetailsValue, 'changeImageOverlay'> & {
  children: ReactNode
}

export default function TreeGroupDetailsProvider({
  children,
  group,
  refetchGroup,
  onLeaveGroup,
}: TreeGroupDetailsProviderProps): JSX.Element {
  const changeImageOverlay = useOverlay()
  const value = useMemo(
    () => ({
      group,
      refetchGroup,
      onLeaveGroup,
      changeImageOverlay,
    }),
    [group, refetchGroup, onLeaveGroup, changeImageOverlay]
  )
  return (
    <TreeGroupDetailsContext.Provider value={value}>
      {children}
      <ChangeGroupImageOverlay group={group} overlay={changeImageOverlay} onGroupImageChange={refetchGroup} />
    </TreeGroupDetailsContext.Provider>
  )
}
